/* eslint-disable no-unused-expressions */
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
  SnackbarContent,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  capitalizeFirstLetter,
  errorColor,
  successColor,
  useAuth,
} from "../../sdk";
import { useForm, useWatch } from "react-hook-form";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { useParams } from "react-router-dom";
import ConfirmationDialog from "../../PrimarySetups/components/ConfirmationDialog";

const formatDate = (dateObj, format) => {
  if (!dateObj) return "";

  const year = dateObj.year?.toString();
  const month = dateObj.month?.toString().padStart(2, "0");
  const day = dateObj.day?.toString().padStart(2, "0");

  switch (format) {
    case "YYYY-MM-DD":
      return `${year}-${month}-${day}`;
    default:
      return `${day}-${month}-${year}`;
  }
};

function applyRoomMappingOrder(getRoomMapping, getTableData) {
  if (!Array.isArray(getRoomMapping) || !Array.isArray(getTableData)) {
    return [];
  }

  const roomMappingById = getRoomMapping.reduce((acc, item) => {
    if (!acc[item.roomId]) {
      acc[item.roomId] = {
        externalSequence: item.externalSequence,
        ratePlans: [],
        _ratePlanKeys: new Set(),
      };
    }
    const key = `${item.roomRateGroupCode}_${item.occupancyType}`;
    if (!acc[item.roomId]._ratePlanKeys.has(key)) {
      acc[item.roomId]._ratePlanKeys.add(key);
      acc[item.roomId].ratePlans.push(item);
    }
    return acc;
  }, {});

  Object.values(roomMappingById).forEach((room) => {
    room.ratePlans.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
    room.sequenceMap = new Map(
      room.ratePlans.map((rp) => [
        `${rp.roomRateGroupCode}_${rp.occupancyType}`,
        rp.sequence,
      ]),
    );
    delete room._ratePlanKeys;
  });

  const orderedRoomIds = Object.keys(roomMappingById).sort(
    (a, b) =>
      (roomMappingById[a].externalSequence ?? 0) -
      (roomMappingById[b].externalSequence ?? 0),
  );

  const tableDataMap = new Map(
    getTableData.map((room) => [String(room.roomId), room]),
  );
  const reorderedTable = [];
  const mappedRoomIds = new Set();

  orderedRoomIds.forEach((roomId) => {
    const matchedRoom = tableDataMap.get(roomId);
    if (matchedRoom) {
      mappedRoomIds.add(roomId);
      const { sequenceMap } = roomMappingById[roomId];
      const orderedRatePlans = [...matchedRoom.ratePlanData].sort((a, b) => {
        const keyA = `${a.roomRateGroupCode}_${a.occupancyType}`;
        const keyB = `${b.roomRateGroupCode}_${b.occupancyType}`;
        const seqA = sequenceMap.get(keyA) ?? 9999;
        const seqB = sequenceMap.get(keyB) ?? 9999;
        return seqA - seqB;
      });
      reorderedTable.push({ ...matchedRoom, ratePlanData: orderedRatePlans });
    }
  });

  return reorderedTable;
}

function transformData(data) {
  if (!Array.isArray(data)) {
    return [];
  }
  return (
    data
      // Sort rooms by externalSequence
      .sort((a, b) => (a.externalSequence ?? 0) - (b.externalSequence ?? 0))
      // Map each room
      .map((room) => {
        // Flatten and merge all rate plans
        const flattenedRatePlans = (room.ratePlanData ?? []).flatMap((plan) =>
          (plan.ratePlanByOccupancy ?? []).map((occ) => ({
            roomRateGroupCode: plan.roomRateGroupCode,
            isPushOPChange: false,
            ...occ,
          })),
        );

        // Sort all rate plans by sequence
        const sortedRatePlans = flattenedRatePlans.sort((a, b) => {
          return (a.sequence ?? 0) - (b.sequence ?? 0);
        });

        return {
          roomId: room.roomId,
          roomName: room.roomName,
          inventory: room.inventory,
          externalSequence: room.externalSequence,
          ratePlanData: sortedRatePlans,
        };
      })
  );
}

const initialValue = {
  pushRatesTable: [],
};

export default function PushResavenueRatesDialog({
  open,
  handleClose,
  currentHotel,
  pushRateFromDate,
  pushRatesToChannel,
}) {
  const { authFetch } = useAuth();
  const { hotelId, FILEID: fileId } = useParams();
  const [roomMappings, setRoomMappings] = useState([]);
  const [
    showDisableOverrideConfirmationPopup,
    setShowDisableOverrideConfirmationPopup,
  ] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    watch,
    control,
    handleSubmit,
  } = useForm({
    defaultValues: initialValue,
    mode: "all",
  });

  const tableData = useWatch({ name: "pushRatesTable", control });

  const getTable = useCallback(async () => {
    const { get } = await authFetch({
      path: `/hotel/${hotelId}/get-push-price-details?date=${formatDate(
        pushRateFromDate,
        "YYYY-MM-DD",
      )}&recommendationId=${fileId}`,
    });
    const { data, error, response } = await get();

    if (response?.ok) {
      const res = data?.[formatDate(pushRateFromDate, "YYYY-MM-DD")];
      const newTableData = transformData(res);
      const newOrderedData = applyRoomMappingOrder(roomMappings, newTableData);

      setValue("pushRatesTable", newOrderedData);
      clearErrors();
    } else {
      setShowSnackbar({
        isError: true,
        message: data?.messageToUser || "Failed to fetch Push Price Details",
      });
      console.error(error);
    }
  }, [
    authFetch,
    hotelId,
    pushRateFromDate,
    fileId,
    roomMappings,
    setValue,
    clearErrors,
  ]);

  const getRoomRatePlan = useCallback(async () => {
    try {
      const { get } = await authFetch({
        path: `/hotel/${hotelId}/get-room-rate-plan`,
      });

      const { data, response } = await get();
      if (data && response?.ok) {
        setRoomMappings(data);
      }
    } catch (err) {
      console.error("Error fetching rooms mapping", err);
    }
  }, [authFetch, hotelId]);

  const disablePushOverride = async (
    roomId,
    roomRateGroupCode,
    occupancyType,
  ) => {
    try {
      const { post } = await authFetch({
        path: `/hotel/${hotelId}/disable-push-override`,
      });
      const payload = {
        recommendationId: Number(fileId),
        date: formatDate(pushRateFromDate, "YYYY-MM-DD"),
        roomId: String(roomId),
        rateID: roomRateGroupCode,
        occupancyType: occupancyType,
      };
      const { error, response } = await post(payload);
      if (response?.ok) {
        getTable();
        setShowSnackbar({
          isError: false,
          message: response?.data?.message,
        });
      } else {
        console.error(error);
        setShowSnackbar({
          isError: true,
          message: "Something Went Wrong",
        });
      }
    } catch (error) {
      console.error(error);
      setShowSnackbar({ isError: true, message: "Something Went Wrong" });
    }
  };

  const updatePushRate = useCallback(
    async (value, pushRateFromDate) => {
      setIsLoading(true);
      try {
        const commonFields = {
          date: formatDate(pushRateFromDate, "YYYY-MM-DD"),
          recommendationId: Number(fileId),
        };

        const frombody = value?.pushRatesTable?.flatMap((room) =>
          room.ratePlanData?.map((plan) => ({
            ...commonFields,
            pushPrice: plan?.isPushOverride
              ? Number(plan?.pushOverridePrice)
              : Number(plan?.pushPrice),
            roomId: String(room?.roomId),
            rateID: String(plan?.roomRateGroupCode),
            overrideReason: plan?.overrideReason,
            occupancyType: plan?.occupancyType,
          })),
        );

        const { post } = await authFetch({
          path: `/hotel/${hotelId}/update-push-price-details`,
        });

        const { data, response } = await post(frombody);
        if (response.ok) {
          pushRatesToChannel(pushRateFromDate);
          getTable();
        } else {
          setShowSnackbar({
            isError: true,
            message: data?.messageToUser || "Failed to update push rates",
          });
        }
      } catch (err) {
        console.error(err);
        setShowSnackbar({
          isError: true,
          message: "Something Went Wrong",
        });
      } finally {
        setIsLoading(false);
      }
    },
    [authFetch, fileId, getTable, hotelId, pushRatesToChannel],
  );

  const handleChangeOverrideReason = (event, roomIndex, ratePlanIndex) => {
    const { value } = event.target;
    if (!value) {
      setError(
        `pushRatesTable.${roomIndex}.ratePlanData.${ratePlanIndex}.overrideReason`,
        {
          type: "custom",
          message: "Please Provide Override Reason",
        },
      );
    } else {
      clearErrors(
        `pushRatesTable.${roomIndex}.ratePlanData.${ratePlanIndex}.overrideReason`,
      );
    }
    setValue(
      `pushRatesTable.${roomIndex}.ratePlanData.${ratePlanIndex}.overrideReason`,
      value,
    );
  };

  const handleChangeOverrideCheckbox = (
    event,
    ratePlan,
    roomIndex,
    ratePlanIndex,
    roomId,
  ) => {
    if (event.target.checked) {
      setValue(
        `pushRatesTable.${roomIndex}.ratePlanData.${ratePlanIndex}.isPushOverride`,
        event.target.checked,
      );
      if (
        !tableData?.[roomIndex]?.ratePlanData?.[ratePlanIndex]
          ?.pushOverridePrice
      ) {
        setError(
          `pushRatesTable.${roomIndex}.ratePlanData.${ratePlanIndex}.pushOverridePrice`,
          {
            type: "custom",
            message: "Price cannot be zero or empty",
          },
        );
      }
    } else {
      setShowDisableOverrideConfirmationPopup({
        roomId: roomId,
        rateId: ratePlan?.roomRateGroupCode,
        occupancyType: ratePlan?.occupancyType,
      });
    }
  };

  const getRateComparison = useCallback(
    (value, roomIndex, ratePlanIndex, type = "show") => {
      const date = new Date(
        pushRateFromDate.year,
        pushRateFromDate.month - 1,
        pushRateFromDate.day,
      );

      const tableRowData = tableData[roomIndex]?.ratePlanData?.[ratePlanIndex];
      const indexOfDay = date.getDay();

      let minThreshold, maxThreshold;

      if (indexOfDay && currentHotel?.midWeek?.includes(indexOfDay)) {
        minThreshold = tableRowData?.minThresholdWeekday;
        maxThreshold = tableRowData?.maxThresholdWeekday;
      } else {
        minThreshold = tableRowData?.minThresholdWeekend;
        maxThreshold = tableRowData?.maxThresholdWeekend;
      }

      const numValue = Number(value);
      if (!numValue) {
        return type === "show" ? `${value}` : `Price cannot be zero or empty`;
      }

      if (minThreshold && numValue < minThreshold) {
        return type === "show" ? (
          <>
            {Math.round(minThreshold)}
            <RedAsterisk>*</RedAsterisk>
          </>
        ) : (
          `Price should be more than ${Math.round(minThreshold)}`
        );
      }

      if (maxThreshold && numValue > maxThreshold) {
        return type === "show" ? (
          <>
            {maxThreshold}
            <RedAsterisk>*</RedAsterisk>
          </>
        ) : (
          `Price should be less than ${maxThreshold}`
        );
      }

      return type === "show" ? `${value}` : null;
    },
    [
      currentHotel.midWeek,
      pushRateFromDate.day,
      pushRateFromDate.month,
      pushRateFromDate.year,
      tableData,
    ],
  );

  const handleChangeSinglePushRate = useCallback(
    (event, roomIndex, ratePlanIndex) => {
      const { value } = event.target;
      const positiveNumberRegex = /^(?:[0-9]+(?:\.[0-9]*)?|\.[0-9]+)?$/;

      if (positiveNumberRegex.test(value)) {
        const errorMessage = getRateComparison(
          value,
          roomIndex,
          ratePlanIndex,
          "validate",
        );

        if (errorMessage) {
          setError(
            `pushRatesTable.${roomIndex}.ratePlanData.${ratePlanIndex}.pushOverridePrice`,
            {
              type: "custom",
              message: errorMessage,
            },
          );
        } else {
          clearErrors(
            `pushRatesTable.${roomIndex}.ratePlanData.${ratePlanIndex}.pushOverridePrice`,
          );
        }

        setValue(
          `pushRatesTable.${roomIndex}.ratePlanData.${ratePlanIndex}.pushOverridePrice`,
          Number(value),
        );
        setValue(
          `pushRatesTable.${roomIndex}.ratePlanData.${ratePlanIndex}.isPushOPChange`,
          true,
        );
        if (
          !tableData?.[roomIndex].ratePlanData[ratePlanIndex]?.overrideReason
        ) {
          setError(
            `pushRatesTable.${roomIndex}.ratePlanData.${ratePlanIndex}.overrideReason`,
            {
              type: "custom",
              message: "Please Provide Override Reason",
            },
          );
        }
      }
    },
    [clearErrors, getRateComparison, setError, setValue, tableData],
  );

  const tableColumns = [
    { key: "roomName", label: "Room Name" },
    { key: "inventory", label: "Inventory" },
    { key: "ratePlanName", label: "Rate Plan Name" },
    { key: "occType", label: "Occ Type" },
    { key: "currentPrice", label: "Current Price" },
    { key: "recommendedPrice", label: "Recommended Price" },
    {
      key: "overridePrice",
      label: "Override Price",
      style: { width: "160px" },
    },
    {
      key: "overrideReason",
      label: "Override Reason",
      style: { width: "220px" },
    },
    { key: "promotionalDiscount", label: "Promotional Discount" },
    { key: "finalPrice", label: "Final Price", style: { minWidth: "100px" } },
  ];

  const onSave = useCallback(
    (values) => {
      updatePushRate(values, pushRateFromDate, true);
    },
    [pushRateFromDate, updatePushRate],
  );

  useEffect(() => {
    getRoomRatePlan();
  }, [getRoomRatePlan]);

  // When mappings are ready, fetch table data
  useEffect(() => {
    getTable();
  }, [roomMappings, getTable]);

  return (
    <StyledChannelDialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle className="content">
        <Typography variant="body1">
          Push Rates to Channel Manager ({formatDate(pushRateFromDate)})
        </Typography>
      </DialogTitle>

      <DialogContent className="content">
        <Box
          sx={{ maxHeight: "330px", overflow: "auto", mb: "8px" }}
          component="form"
        >
          <Table>
            <TableHead>
              <StyledTableRow>
                {tableColumns.map((column) => (
                  <HeaderTableCell key={column.key} sx={column?.style}>
                    {column.label}
                  </HeaderTableCell>
                ))}
              </StyledTableRow>
            </TableHead>

            {/* Extracted TableBody */}
            <PushRatesTableBody
              tableData={tableData}
              watch={watch}
              errors={errors}
              handleChangeSinglePushRate={handleChangeSinglePushRate}
              handleChangeOverrideCheckbox={handleChangeOverrideCheckbox}
              getRateComparison={getRateComparison}
              handleChangeOverrideReason={handleChangeOverrideReason}
            />
          </Table>
        </Box>
        <LoadingButton
          loading={isLoading}
          variant="contained"
          type="submit"
          disabled={Object.keys(errors).length > 0 || isLoading}
          onClick={handleSubmit(onSave)}
          sx={{ textTransform: "none" }}
        >
          Submit Rates to CM
        </LoadingButton>
      </DialogContent>
      <CloseIcon className="closeButton" onClick={handleClose} />

      <ConfirmationDialog
        warningIcon
        handleCloseConfirmation={() =>
          setShowDisableOverrideConfirmationPopup(null)
        }
        confirmationText={
          "Are you sure you want to Disable Override Price for this date? Click yes to confirm."
        }
        onSuccess={async () => {
          await disablePushOverride(
            showDisableOverrideConfirmationPopup.roomId,
            showDisableOverrideConfirmationPopup.rateId,
            showDisableOverrideConfirmationPopup.occupancyType,
          );
          setShowDisableOverrideConfirmationPopup(null);
        }}
        open={Boolean(showDisableOverrideConfirmationPopup)}
      />

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        open={!!showSnackbar}
        autoHideDuration={3000}
        onClose={() => setShowSnackbar(null)}
      >
        {showSnackbar && (
          <SnackbarContent
            sx={{
              backgroundColor: showSnackbar.isError ? errorColor : successColor,
              color: "#fff",
            }}
            message={showSnackbar.message}
          />
        )}
      </Snackbar>
    </StyledChannelDialog>
  );
}

const PushRatesTableBody = ({
  tableData,
  watch,
  errors,
  handleChangeSinglePushRate,
  handleChangeOverrideCheckbox,
  handleChangeOverrideReason,
  getRateComparison,
}) => (
  <TableBody>
    {tableData?.map((room, roomIndex) =>
      room.ratePlanData?.map((ratePlan, ratePlanIndex) => (
        <StyledTableRow key={`${roomIndex}-${ratePlanIndex}`}>
          {/* Show roomName + inventory only on the first row of this room */}
          {ratePlanIndex === 0 && (
            <>
              <CustomTableCell rowSpan={room.ratePlanData.length}>
                {room.roomName || "-"}
              </CustomTableCell>
              <CustomTableCell rowSpan={room.ratePlanData.length}>
                {room.inventory ?? "-"}
              </CustomTableCell>
            </>
          )}

          <CustomTableCell>{ratePlan.roomRatePlanName || "-"}</CustomTableCell>
          <CustomTableCell>
            {ratePlan.occupancyType
              ? capitalizeFirstLetter(ratePlan.occupancyType)
              : "-"}
          </CustomTableCell>
          <CustomTableCell>
            {Math.round(ratePlan.currentPrice || 0)}
          </CustomTableCell>
          <CustomTableCell>
            {getRateComparison(
              ratePlan.recommendedPrice,
              roomIndex,
              ratePlanIndex,
              "show",
            )}
          </CustomTableCell>
          <CustomTableCell
            style={{
              backgroundColor: !ratePlan?.isPushOverride ? "#0000000A" : "",
            }}
          >
            <Stack
              direction={"row"}
              alignItems={"center"}
              justifyContent={"center"}
              paddingRight={1}
            >
              <Checkbox
                size="small"
                checked={ratePlan?.isPushOverride}
                onChange={(event) =>
                  handleChangeOverrideCheckbox(
                    event,
                    ratePlan,
                    roomIndex,
                    ratePlanIndex,
                    room.roomId,
                  )
                }
              />
              <TextField
                disabled={!ratePlan?.isPushOverride}
                variant="standard"
                value={
                  ratePlan.pushOverridePrice
                    ? Math.round(ratePlan?.pushOverridePrice)
                    : ""
                }
                onChange={(e) =>
                  handleChangeSinglePushRate(e, roomIndex, ratePlanIndex)
                }
                inputProps={{
                  style: { fontSize: "12px", textAlign: "center" },
                }}
                error={
                  !!errors?.pushRatesTable?.[roomIndex]?.ratePlanData?.[
                    ratePlanIndex
                  ]?.pushOverridePrice
                }
                helperText={
                  errors?.pushRatesTable?.[roomIndex]?.ratePlanData?.[
                    ratePlanIndex
                  ]?.pushOverridePrice?.message || ""
                }
              />
            </Stack>
          </CustomTableCell>
          <CustomTableCell
            style={{
              backgroundColor:
                ratePlan?.overrideReason || ratePlan?.isPushOPChange
                  ? "inherit"
                  : "#0000000A",
            }}
          >
            {ratePlan?.isPushOPChange ? (
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"center"}
                padding={1}
              >
                <TextField
                  required
                  variant="standard"
                  value={
                    tableData?.[roomIndex]?.ratePlanData?.[ratePlanIndex]
                      ?.overrideReason
                  }
                  sx={{ minWidth: "200px" }}
                  onChange={(e) =>
                    handleChangeOverrideReason(e, roomIndex, ratePlanIndex)
                  }
                  error={
                    !!errors?.pushRatesTable?.[roomIndex]?.ratePlanData?.[
                      ratePlanIndex
                    ]?.overrideReason
                  }
                  helperText={
                    errors?.pushRatesTable?.[roomIndex]?.ratePlanData?.[
                      ratePlanIndex
                    ]?.overrideReason
                      ? errors?.pushRatesTable?.[roomIndex]?.ratePlanData?.[
                          ratePlanIndex
                        ]?.overrideReason?.message
                      : ""
                  }
                  inputProps={{
                    style: {
                      fontSize: "10px",
                      textAlign: "center",
                    },
                  }}
                />
              </Stack>
            ) : (
              <Tooltip title={ratePlan?.overrideReason}>
                {ratePlan?.overrideReason
                  ? `${ratePlan?.overrideReason?.slice(0, 25)}...`
                  : ""}
              </Tooltip>
            )}
          </CustomTableCell>
          <CustomTableCell
            style={{
              backgroundColor: "#0000000A",
            }}
          >
            {ratePlan.promotionalDiscount || "-"}
          </CustomTableCell>
          <CustomTableCell>
            {Math.round(
              watch(
                `pushRatesTable.${roomIndex}.ratePlanData.${ratePlanIndex}.pushOverridePrice`,
              ) ||
                ratePlan.pushPrice ||
                0,
            )}
          </CustomTableCell>
        </StyledTableRow>
      )),
    )}
  </TableBody>
);

const StyledChannelDialog = styled(Dialog)(() => ({
  ".MuiPaper-root": { overflow: "visible" },
  ".closeButton": {
    position: "absolute",
    right: "10px",
    top: "10px",
    cursor: "pointer",
  },
  ".content": {
    padding: "7px 24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    overflow: "visible",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme, selected }) => ({
  backgroundColor: theme.palette.common.white,
  ...(selected && {
    backgroundColor: "rgba(25, 118, 210, 0.16)",
    border: "2px solid red",
    "& > td": { backgroundColor: "rgba(25, 118, 210, 0.16)" },
  }),
}));

const HeaderTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: "#306fbc",
  color: theme.palette.common.white,
  padding: "10px",
  textAlign: "center",
  borderRight: "1px solid #444",
  [`&.${tableCellClasses.head}`]: { fontSize: "12px" },
}));

const CustomTableCell = styled(TableCell)(() => ({
  padding: "0px",
  textAlign: "center",
  border: "1px solid #eee",
  color: "#555",
  [`&.${tableCellClasses.body}`]: { fontSize: "10px" },
}));

const RedAsterisk = styled("span")({
  color: "red",
});
